import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { House3Icon, LockIcon, Star2Icon } from "@/shared/icons";
import BadgeAvita from "@/shared/widgets/BadgeAvita";
import { EstateSelectCardTypes } from "@/domains/estate/constants/estateSelectCardTypes";
import { Estate } from "@/domains/estate/slice";
import getEstateImage from "@/domains/estate/helpers/getEstateImage";
import EstateBadge from "@/domains/estate/components/EstateBadge";

type Props = {
  type: EstateSelectCardTypes;
  estate?: Estate;
  changeSelectedEstateId?: (id: number) => void;
  isResident?: boolean;
  isAvita?: boolean;
  setPurchaseModal?: (arg0: boolean) => void;
};

export default function EstateSlot({
  type,
  estate,
  changeSelectedEstateId,
  isResident,
  isAvita,
  setPurchaseModal,
}: Props) {
  const { t } = useTranslation("estate");
  const estateImage = estate?.id ? getEstateImage(estate, "medium") : "";
  const estateLevel = estate?.level ? t(`estate.level.${estate.level}`) : "";

  return (
    <div
      className={clsx(
        "group relative flex justify-center items-center h-[15.125rem] bg-dark-800/30 border border-dashed border-[#1C1C1C] rounded-2xl overflow-hidden",
        type === EstateSelectCardTypes.Blocked && "hover:border-transparent",
      )}
    >
      {type === EstateSelectCardTypes.Purchased && (
        <button
          className="relative flex flex-col justify-end size-full"
          onClick={() => changeSelectedEstateId(estate.id)}
          type="button"
        >
          <div
            className="absolute top-0 left-0 w-full h-[26.5rem] bg-cover bg-no-repeat bg-center"
            style={{ backgroundImage: `url(${estateImage})` }}
          />
          {isResident && (
            <EstateBadge
              name="success"
              text={t("estate.residence")}
              className="mt-4 ml-4 mb-auto"
            />
          )}
          {isAvita && <BadgeAvita />}
          <div className="relative z-1 flex flex-col items-start p-7.5">
            <div className="flex self-start items-center gap-0.5 mb-2.5 py-[0.3125rem] pl-[0.3125rem] pr-[0.4375rem] bg-white rounded-[0.3125rem]">
              <Star2Icon size={14} className="text-dark-800" />
              <p className="text-12 text-dark-800 font-bold uppercase">{estateLevel}</p>
            </div>
            <p className="text-18 font-bold">
              {t(`estate.type.${estate.type}.name`)} #{estate?.id}
            </p>
            <p className="text-15 font-medium text-white/50">{estate?.address}</p>
          </div>
          <span
            className="absolute bottom-0 left-0 w-full h-60"
            style={{
              background:
                "linear-gradient(0deg, #181818 0%, #181818 30%, rgba(24, 24, 24, 0.90) 60%,  rgba(24, 24, 24, 0.6) 80%, rgba(24, 24, 24, 0.2) 90%, transparent 100%)",
            }}
          />
        </button>
      )}
      {type === EstateSelectCardTypes.Empty && (
        <div className="flex flex-col justify-center items-center gap-3.5">
          <House3Icon size={28} className="text-white/10" />
          <p className="text-10 font-bold text-white/30 uppercase">{t("estate.emptyPlace")}</p>
        </div>
      )}
      {type === EstateSelectCardTypes.Blocked && (
        <button type="button" className="size-full" onClick={() => setPurchaseModal(true)}>
          <div className="absolute z-1 top-0 bottom-0 size-full border border-dashed border-accent-solid rounded-2xl opacity-0 transition-opacity duration-200 group-hover:opacity-100">
            <span
              className="absolute -top-[1px] -left-[1px] -bottom-[1px] -right-[1px]"
              style={{
                background:
                  "linear-gradient(to top, rgba(24, 24, 24, 0) 0%, rgba(24, 24, 24, 0.4) 30%, rgba(24, 24, 24, 0.8) 100%)",
              }}
            />
            <span className="absolute top-0 left-0 size-full bg-[linear-gradient(180deg,rgba(171,41,41,0.03)_0%,rgba(171,41,41,0.10)_100%)]" />
          </div>
          <div className="relative z-3 flex flex-col justify-center items-center gap-3.5">
            <LockIcon size={28} className="text-accent-solid" />
            <p className="text-10 font-bold text-white/30 uppercase transition-all duration-200 group-hover:text-accent-solid">
              {t("estate.unlockSlot")}
            </p>
          </div>
        </button>
      )}
    </div>
  );
}
